import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import {
  Alert,
  BaseBadge,
  Button,
  CampaignIcon,
  Card,
  FeaturedIcon,
  MarketIntelligenceIcon,
  RefreshIcon,
  RuleAudienceIcon,
  RuleTraitIcon,
  RuleViewIcon,
} from '@/lib';
import { RouteConstants } from '@/features/router/RouteConstants';
import { GetMarketIntelligenceReportResponse } from '@/services/endpoints/marketIntelligence';
import { ReportGeneratingScreen } from './ReportGeneratingScreen';
import {
  MarketIntelligenceAction,
  MarketIntelligenceCompetitor,
  MarketIntelligenceMetric,
  MarketIntelligenceReportData,
  MarketIntelligenceReportFailure,
} from '../types';

interface MarketIntelligenceReportProps {
  report: MarketIntelligenceReportData | null;
  reportId: string | null;
  isGenerating: boolean;
  isLoadingReportHistory: boolean;
  showLatestReportWarning: boolean;
  onReportReady: (response: GetMarketIntelligenceReportResponse) => void;
  onReportError: (failure: MarketIntelligenceReportFailure) => void;
  onRegenerate: () => Promise<void>;
}

const competitorIcons = [MarketIntelligenceIcon, RuleViewIcon, RuleTraitIcon];

const REPORT_META_ITEMS = [
  { id: 'analyzedDomain', translationKey: 'report.meta.analyzedDomain' },
  { id: 'industry', translationKey: 'report.meta.industry' },
  { id: 'scopeLabel', translationKey: 'report.meta.scope' },
  { id: 'generatedAt', translationKey: 'report.meta.generatedAt' },
] as const satisfies ReadonlyArray<{
  id: keyof Pick<MarketIntelligenceReportData, 'analyzedDomain' | 'industry' | 'scopeLabel' | 'generatedAt'>;
  translationKey: string;
}>;

const ReportMetaItem: FC<{ value: string; label: string }> = ({ value, label }) => (
  <div className='min-w-0'>
    <p className='text-primary-900 truncate text-sm font-semibold'>{value}</p>
    <p className='text-xs text-primary-600'>{label}</p>
  </div>
);

const CompetitorListItem: FC<{ competitor: MarketIntelligenceCompetitor; index: number; threatLabel: string }> = ({ competitor, index, threatLabel }) => {
  const Icon = competitorIcons[index % competitorIcons.length];

  return (
    <Card className='items-center gap-4 border-primary-border bg-white p-4 drop-shadow-none'>
      <FeaturedIcon color='gray' size='sm' type='modern'>
        <Icon className='size-4' />
      </FeaturedIcon>
      <div className='min-w-0 flex-1'>
        <p className='text-primary-900 truncate text-sm font-semibold'>{competitor.name}</p>
        <p className='text-primary-600 mt-1 text-xs leading-5'>{competitor.description}</p>
      </div>
      {typeof competitor.threat === 'number' ? (
        <div className='shrink-0 text-right'>
          <p className='text-2xl font-semibold leading-none text-brand-700'>{competitor.threat}</p>
          <p className='text-primary-500 mt-1 text-[10px] font-semibold uppercase tracking-[0.08em]'>{threatLabel}</p>
        </div>
      ) : null}
    </Card>
  );
};

const MetricCard: FC<{ metric: MarketIntelligenceMetric; tone: 'success' | 'warning' }> = ({ metric, tone }) => {
  const hasProgressMetric =
    typeof metric.metricValue === 'number' && typeof metric.maxValue === 'number' && Boolean(metric.metricLabel) && Boolean(metric.metricDisplay);
  const progressWidth = hasProgressMetric ? `${Math.min((metric.metricValue / metric.maxValue) * 100, 100)}%` : '0%';

  return (
    <Card className='block border-primary-border bg-white p-4 drop-shadow-none'>
      <p className='text-primary-900 text-sm font-semibold'>{metric.title}</p>
      <p className='text-primary-600 mt-1 min-h-10 text-xs leading-5'>{metric.description}</p>
      {hasProgressMetric ? (
        <div className='mt-4'>
          <div className='text-primary-600 mb-1 flex items-center justify-between text-[11px]'>
            <span>{metric.metricLabel}</span>
            <span className='text-primary-500 font-medium'>{metric.metricDisplay}</span>
          </div>
          <div className='bg-primary-200 h-1.5 overflow-hidden rounded-full'>
            <div className={twMerge('h-full rounded-full', tone === 'success' ? 'bg-green-500' : 'bg-orange-500')} style={{ width: progressWidth }} />
          </div>
        </div>
      ) : null}
    </Card>
  );
};

const ActionCard: FC<{ action: MarketIntelligenceAction }> = ({ action }) => {
  const history = useHistory();

  const handleClick = () => {
    history.push(action.ctaType === 'audience' ? RouteConstants.audiences : RouteConstants.campaigns);
  };

  return (
    <Card className='block border-primary-border bg-white p-4 drop-shadow-none'>
      <p className='text-[11px] font-semibold uppercase tracking-[0.08em] text-brand-700'>{action.eyebrow}</p>
      <p className='mt-4 text-sm font-medium leading-6 text-primary-600'>{action.description}</p>
      <p className='mt-4 text-lg font-semibold text-primary-900'>{action.title}</p>
      <Button intent='primary' size='md' className='mt-5' onClick={handleClick}>
        {action.ctaType === 'audience' ? <RuleAudienceIcon className='size-4' /> : <CampaignIcon className='size-4' />}
        <span>{action.ctaLabel}</span>
      </Button>
    </Card>
  );
};

export const MarketIntelligenceReport: FC<MarketIntelligenceReportProps> = ({
  report,
  reportId,
  isGenerating,
  isLoadingReportHistory,
  showLatestReportWarning,
  onReportReady,
  onReportError,
  onRegenerate,
}) => {
  const { t } = useTranslation('marketIntelligence');

  if (isGenerating) {
    return <ReportGeneratingScreen reportId={reportId} onComplete={onReportReady} onError={onReportError} />;
  }

  if (isLoadingReportHistory) {
    return (
      <Card className='block rounded-lg border-primary-border bg-white p-6 drop-shadow-none'>
        <div className='py-12 text-center'>
          <h3 className='mb-2 text-lg font-semibold text-primary-900'>{t('report.loading.title')}</h3>
        </div>
      </Card>
    );
  }

  if (!report) {
    return (
      <div className='w-full space-y-5'>
        {showLatestReportWarning ? (
          <Alert type='WARNING' title={t('report.latestGenerationFailed.title')} description={t('report.latestGenerationFailed.description')} />
        ) : null}
        <Card className='block rounded-lg border-primary-border bg-white p-6 drop-shadow-none'>
          <div className='py-12 text-center'>
            <h3 className='mb-2 text-lg font-semibold text-primary-900'>{t('report.empty.title')}</h3>
            <p className='text-primary-600'>{t('report.empty.description')}</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className='w-full space-y-5'>
      {showLatestReportWarning ? (
        <Alert type='WARNING' title={t('report.latestGenerationFailed.title')} description={t('report.latestGenerationFailed.description')} />
      ) : null}

      <Card className='block border-primary-border bg-white p-6 drop-shadow-none'>
        <div className='flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between'>
          <div className='grid flex-1 gap-4 sm:grid-cols-2 xl:grid-cols-4'>
            {REPORT_META_ITEMS.map((item) => (
              <ReportMetaItem key={item.id} value={report[item.id]} label={t(item.translationKey)} />
            ))}
          </div>
          <div className='flex flex-wrap gap-3'>
            <Button intent='secondary' size='md' onClick={onRegenerate}>
              <RefreshIcon className='size-4' />
              <span>{t('report.actions.regenerate')}</span>
            </Button>
          </div>
        </div>
      </Card>

      <div className='grid w-full grid-cols-2 gap-5'>
        <Card className='block border-primary-border bg-white p-5 drop-shadow-none'>
          <div className='flex items-start justify-between gap-3'>
            <div>
              <h3 className='text-2xl font-semibold text-primary-900'>{t('report.competitors.title')}</h3>
              <p className='mt-1 text-sm text-grey-600'>{t('report.competitors.description')}</p>
            </div>
            <BaseBadge className='rounded-full bg-brand-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-brand-700'>
              {t('report.competitors.badge')}
            </BaseBadge>
          </div>

          <div className='mt-5 space-y-3'>
            {report.competitors.map((competitor, index) => (
              <CompetitorListItem key={competitor.id} competitor={competitor} index={index} threatLabel={t('report.competitors.threat')} />
            ))}
          </div>
        </Card>

        <Card className='block border-primary-border bg-white p-5 drop-shadow-none'>
          <div>
            <h3 className='text-2xl font-semibold text-primary-900'>{t('report.gapAnalysis.title')}</h3>
            <p className='mt-1 text-sm text-primary-600'>{t('report.gapAnalysis.description')}</p>
          </div>

          <div className='mt-5 grid gap-4 md:grid-cols-2'>
            <div className='space-y-3'>
              <p className='flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.08em] text-green-600'>
                <span className='inline-block size-2 rounded-full bg-green-500'></span>
                {t('report.gapAnalysis.strengths')}
              </p>
              {report.strengths.map((metric) => (
                <MetricCard key={metric.id} metric={metric} tone='success' />
              ))}
            </div>

            <div className='space-y-3'>
              <p className='flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-orange-600'>
                <span className='inline-block size-2 rounded-full bg-orange-500'></span>
                {t('report.gapAnalysis.edge')}
              </p>
              {report.competitorsEdge.map((metric) => (
                <MetricCard key={metric.id} metric={metric} tone='warning' />
              ))}
            </div>
          </div>
        </Card>
      </div>

      <Card className='block border-primary-border bg-primary-background p-5 drop-shadow-none'>
        <div>
          <h3 className='text-3xl font-bold text-primary-900'>
            <span className='text-brand-700'>{t('report.strategicAction.titlePrefix')}</span> {t('report.strategicAction.titleSuffix')}
          </h3>
          <p className='text-grey-600 mt-1 text-sm'>{t('report.strategicAction.description')}</p>
        </div>

        <div className='mt-5 grid gap-4 lg:grid-cols-2'>
          {report.actions.map((action) => (
            <ActionCard key={action.id} action={action} />
          ))}
        </div>
      </Card>
    </div>
  );
};
